<script setup lang="ts">
    import { ref, computed, watch, nextTick } from 'vue'
    import type { PropType } from 'vue'

    import type { WeightsComparerReadModel } from "@Criteria/Weights/Domain/readmodels"
    import type { CriterionReadModel } from "@Criteria/Hierarchies/Domain/readmodels"

    import { KendoChart, KendoChartDataSerie, KendoChartOptions } from "@Kendo/Components/KendoChart"
    import { ellipsis, getDistinctColor, roundDecimal } from "@common/Common"

    // ---------------- props / emits ----------------

    const props = defineProps({
        propOpened: { type: Boolean, required: true, default: false },
        propRm: { type: Object as PropType<WeightsComparerReadModel | null>, default: null }
    })

    const emit = defineEmits<{
        (e: 'close-dialog'): void
    }>()

    // ---------------- local state ----------------

    const chart = ref<KendoChart | null>(null)
    const selectedContextId = ref<number | null>(null)

    // ---------------- computed ----------------

    const opened = computed(() => props.propOpened)
    const rm = computed(() => props.propRm)

    const context = computed(() =>
        rm.value?.Criteria.find(a => a.CriterionId === selectedContextId.value)
    )

    const parents = computed(() =>
        rm.value
            ? rm.value.Criteria.filter(a =>
                rm.value!.Criteria.some(b => b.ParentId === a.CriterionId))
            : []
    )

    // ---------------- watchers ----------------

    // initialize context when rm arrives
    watch(rm, (val) =>
    {
        if (val && selectedContextId.value == null)
        {
            selectedContextId.value = val.ContextId ?? val.Criteria[0]?.CriterionId ?? null
        }
    })

    // re-render chart when context changes
    watch(selectedContextId, async (val) =>
    {
        if (val != null)
        {
            await nextTick()
            renderChart()
        }
    })

    watch(opened, async (val) =>
    {
        if (val && selectedContextId.value != null)
        {
            await nextTick()
            renderChart()
        }

        if (!val)
        {
            selectedContextId.value = null
        }
    })


    // ---------------- actions ----------------

    function setContext(c: CriterionReadModel)
    {
        selectedContextId.value = c.CriterionId
    }

    function close()
    {
        emit('close-dialog')
    }

    // ---------------- chart rendering ----------------

    function renderChart()
    {
        if (!rm.value || selectedContextId.value == null) return

        if (chart.value)
        {
            chart.value.Destroy?.()
            chart.value = null
        }


        const options = new KendoChartOptions()
        options.SetBarSeriesType()
        options.EnableSeriesStack100()
        options.SetCategoryAxis(false, false)
        options.SetValueAxis(null, false, false)
        options.SetLegend(true, "bottom", 35)
        options.SetTooltip(true, "<small>#= dataItem.Category #</small><br> #= dataItem.CriterionName #: #= value #%")

        const c = KendoChart.CreateChart(options)
        chart.value = c

        const subcriteria = rm.value.Criteria.filter(a => a.ParentId === selectedContextId.value)

        const series: KendoChartDataSerie[] = subcriteria.map((crit, i) =>
        {
            const dataItems = rm.value!.WeightsSources.map(ws =>
            {
                const weight = ws.Weights.find(a => a.CriterionId === crit.CriterionId)!.LocalWeight
                const value = roundDecimal(100 * weight, 0)

                return {
                    Category: ellipsis(ws.Name, 40),
                    CriterionName: crit.Name,
                    Value: value
                }
            })

            const serie = new KendoChartDataSerie()
            serie.SetName(crit.Name)
            serie.SetField("Value")
            serie.SetCategoryField("Category")
            serie.SetColor(getDistinctColor(i))
            serie.SetData(dataItems)

            return serie
        })

        if (series.length > 0)
        {
            series[0].SetLabels(true, "#= category #", "1rem Arial", "insideBase")
            series[0].SetLabelsColor("#fff")
        }

        c.SetDataSeries(series)

        const minHeightBars = rm.value.WeightsSources.length * 60
        const minVhPxl = 300
        c.SetHeight(Math.max(minHeightBars, minVhPxl))

        c.Render1('Weights')
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Weights comparison</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="rm">
                            <div class="dropdown">
                                <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    {{ context?.Name }}
                                </button>

                                <div class="dropdown-menu" style="max-height: 300px; overflow-y:auto;">
                                    <a class="dropdown-item"
                                       v-for="c in parents"
                                       :key="c.CriterionId"
                                       @click="setContext(c)">
                                        <span>{{ c.Name }}</span><br />
                                        <small>{{ c.Path }}</small>
                                    </a>
                                </div>
                            </div>

                            <br />

                            <div id="Chart-Weights"></div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>